
export type LanguageCode = 'en' | 'ja' | 'de' | 'fr' | 'zh-CN' | 'zh-TW' | 'es' | 'pt' | 'ar' | 'ru';

export class I18nManager {
    private static instance: I18nManager;
    private currentLang: LanguageCode = 'zh-CN';
    private translations: Record<string, string> = {};
    private isLoaded: boolean = false;

    private constructor() {}

    public static getInstance(): I18nManager {
        if (!I18nManager.instance) {
            I18nManager.instance = new I18nManager();
        }
        return I18nManager.instance;
    }

    /**
     * 初始化语言，尝试从本地存储或浏览器设置获取
     */
    public async init(): Promise<void> {
        const savedLang = localStorage.getItem('game_lang') as LanguageCode;
        const browserLang = navigator.language;
        
        let defaultLang: LanguageCode = 'en';
        if (savedLang) {
            defaultLang = savedLang;
        } else if (browserLang.startsWith('zh')) {
            defaultLang = browserLang.includes('TW') || browserLang.includes('HK') ? 'zh-TW' : 'zh-CN';
        } else if (browserLang.startsWith('ja')) {
            defaultLang = 'ja';
        } else if (browserLang.startsWith('de')) {
            defaultLang = 'de';
        } else if (browserLang.startsWith('fr')) {
            defaultLang = 'fr';
        } else if (browserLang.startsWith('es')) {
            defaultLang = 'es';
        } else if (browserLang.startsWith('pt')) {
            defaultLang = 'pt';
        } else if (browserLang.startsWith('ar')) {
            defaultLang = 'ar';
        } else if (browserLang.startsWith('ru')) {
            defaultLang = 'ru';
        }

        await this.setLanguage(defaultLang);
    }

    /**
     * 切换语言并加载对应的 JSON 文件
     * @param lang 语言代码
     */
    public async setLanguage(lang: LanguageCode): Promise<void> {
        if (this.currentLang === lang && this.isLoaded) return;

        try {
            // 使用绝对路径，依赖 Cloudflare 的 ETag 缓存管理
            const response = await fetch(`/assets/locales/${lang}.json`);
            if (!response.ok) throw new Error(`Failed to load language: ${lang}`);
            
            this.translations = await response.json();
            this.currentLang = lang;
            this.isLoaded = true;
            
            // 持久化选择
            localStorage.setItem('game_lang', lang);
            
            // 同步 DOM 属性
            document.documentElement.lang = lang;
            document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
            
            // 自动翻译带有 data-i18n 属性的元素
            this.translatePage();
            
            console.log(`Language switched to: ${lang}`);
        } catch (error) {
            console.error('I18n load error:', error);
            // 如果加载失败且不是英文，尝试退回到英文
            if (lang !== 'en') {
                await this.setLanguage('en');
            }
        }
    }

    /**
     * 获取翻译文本
     * @param key 翻译键值
     * @param params 替换变量
     */
    public t(key: string, params?: Record<string, string | number>): string {
        // 如果还未加载完成，返回空字符串防止显示 Key 名字
        if (!this.isLoaded) return '';
        
        let text = this.translations[key] || key;
        
        if (params) {
            Object.keys(params).forEach(p => {
                text = text.replace(`{${p}}`, String(params[p]));
            });
        }
        
        return text;
    }

    /**
     * 自动翻译页面中带有 data-i18n 属性的 DOM 元素
     */
    public translatePage(): void {
        if (!this.isLoaded) return;

        const elements = document.querySelectorAll('[data-i18n]');
        elements.forEach(el => {
            const key = el.getAttribute('data-i18n');
            if (key) {
                const translation = this.t(key);
                if (translation === '') return; // 跳过未加载的情况
                
                if (el instanceof HTMLInputElement && (el.type === 'text' || el.type === 'search')) {
                    el.placeholder = translation;
                } else {
                    el.textContent = translation;
                }
            }
        });

        // 处理带 title 翻译的元素
        const titleElements = document.querySelectorAll('[data-i18n-title]');
        titleElements.forEach(el => {
            const key = el.getAttribute('data-i18n-title');
            if (key) {
                (el as HTMLElement).title = this.t(key);
            }
        });
    }

    public getCurrentLanguage(): LanguageCode {
        return this.currentLang;
    }
}

export const i18n = I18nManager.getInstance();
